import { useCallback, useMemo } from 'react';
import { usePermission, useRole } from '../rbac/hooks';
import {
  applyViewModeToTaskCaseDetailsPath,
  buildTaskCaseDetailsPath,
  canUpdateTaskCaseDetails,
  type TaskCaseDetailsTabId,
} from '../pages/task-case/taskCaseDetailsAccess';
import {
  appendReturnStateToTaskCasePath,
  type TaskCaseDetailsReturnState,
} from '../pages/task-case/taskCaseDetailsReturnNavigation';

type TaskCaseLinkInput = {
  caseRequestId: string;
  userTaskId?: string;
  viewMode?: boolean;
  /** Open Case Update tab when navigating in update mode (Update icon). */
  detailsTab?: TaskCaseDetailsTabId;
};

type TaskCaseLink = {
  to: string;
  state?: { taskCaseReturn: TaskCaseDetailsReturnState };
};

type UseTaskCaseDetailsNavigationOptions = {
  returnState?: TaskCaseDetailsReturnState;
};

export function useTaskCaseDetailsNavigation(options?: UseTaskCaseDetailsNavigationOptions) {
  const { roleCode } = useRole();
  const { has } = usePermission();
  const returnState = options?.returnState;

  const canUpdate = useMemo(() => canUpdateTaskCaseDetails(roleCode, has), [roleCode, has]);

  const taskCasePath = useCallback(
    (input: TaskCaseLinkInput) =>
      buildTaskCaseDetailsPath({
        caseRequestId: input.caseRequestId,
        userTaskId: input.userTaskId,
        viewMode: input.viewMode ?? !canUpdate,
        detailsTab: input.detailsTab,
      }),
    [canUpdate],
  );

  const taskCaseLink = useCallback(
    (input: TaskCaseLinkInput): TaskCaseLink => {
      let to = taskCasePath(input);
      if (returnState) {
        to = appendReturnStateToTaskCasePath(to, returnState);
        return { to, state: { taskCaseReturn: returnState } };
      }
      return { to };
    },
    [returnState, taskCasePath],
  );

  const applyViewModeIfNeeded = useCallback(
    (path: string) => applyViewModeToTaskCaseDetailsPath(path, roleCode, has),
    [roleCode, has],
  );

  return { canUpdate, taskCasePath, taskCaseLink, applyViewModeIfNeeded };
}
